import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import RichTextEditor from '../components/RichTextEditor';

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  title: '',
  body: '',
  publishDate: today(),
  pinned: false
});

const AdminConferenceDashboard = () => {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/conference');
    } else {
      loadUpdates();
    }
  }, [navigate]);

  const loadUpdates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conference-updates`);
      if (!response.ok) throw new Error('Failed to load updates');
      const data = await response.json();
      setUpdates(data);
      setError('');
    } catch (err) {
      setError('Failed to load updates: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    try {
      const token = sessionStorage.getItem('adminToken');
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${API_URL}/api/conference-updates/${editingId}`
        : `${API_URL}/api/conference-updates`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save update');
      }

      await loadUpdates();
      resetForm();
      setShowForm(false);
    } catch (err) {
      setError('Failed to save: ' + err.message);
    }
  };

  const handleEdit = (update) => {
    setFormData({
      title: update.title || '',
      body: update.body || '',
      publishDate: update.publishDate ? update.publishDate.slice(0, 10) : today(),
      pinned: !!update.pinned
    });
    setEditingId(update._id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this update?')) return;
    try {
      const token = sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/conference-updates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete');
      await loadUpdates();
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setEditingId(null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin/conference');
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '2rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>
              🏛️ Conference Updates
            </h1>
            <p style={{ color: '#64748b', marginTop: '0.5rem' }}>
              Publish news, deadlines and program updates on the Conferences page
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Logout
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            border: '1px solid #fca5a5'
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: '2px solid #2563eb'
          }}>
            <h2 style={{ color: '#1e293b', marginBottom: '1.5rem' }}>
              {editingId ? '✏️ Edit Update' : '➕ New Update'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Workshop submission deadline extended"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                    Publish date
                  </label>
                  <input
                    type="date"
                    value={formData.publishDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, publishDate: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', paddingTop: '1.6rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#1e293b', fontWeight: '500' }}>
                    <input
                      type="checkbox"
                      checked={formData.pinned}
                      onChange={(e) => setFormData(prev => ({ ...prev, pinned: e.target.checked }))}
                      style={{ marginRight: '0.6rem', width: '18px', height: '18px' }}
                    />
                    📌 Pin to top
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                  Body
                </label>
                <RichTextEditor
                  value={formData.body}
                  onChange={(html) => setFormData(prev => ({ ...prev, body: html }))}
                  placeholder="Write the update — use the toolbar for formatting and links..."
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {editingId ? 'Save changes' : 'Publish update'}
                </button>
                <button
                  type="button"
                  onClick={() => { resetForm(); setShowForm(false); }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#64748b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {!showForm && (
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              marginBottom: '2rem'
            }}
          >
            ➕ New Update
          </button>
        )}

        {/* List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading…</div>
        ) : updates.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            No updates yet. Create the first one!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {updates.map((u) => (
              <div key={u._id} style={{
                backgroundColor: 'white',
                padding: '1.25rem 1.5rem',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
                borderLeft: u.pinned ? '4px solid #f59e0b' : '4px solid #2563eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 300px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                      {u.pinned && <span style={{ fontSize: '0.85rem' }}>📌</span>}
                      <h3 style={{ color: '#1e293b', fontSize: '1.15rem', fontWeight: 600, margin: 0 }}>
                        {u.title}
                      </h3>
                    </div>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                      {formatDate(u.publishDate)}
                    </p>
                    {u.body && (
                      <div
                        style={{ color: '#475569', fontSize: '0.95rem' }}
                        dangerouslySetInnerHTML={{ __html: u.body }}
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleEdit(u)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(u._id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConferenceDashboard;
